/**
 * Locale → string map. The wire format uses keys like `de_DE`, `en_US`. Some
 * locales may be missing or empty — every consumer must tolerate that and
 * fall back via {@link pickLocale}.
 */
export type LocalizedString = Readonly<Partial<Record<string, string>>>;

/**
 * Pick the user-preferred locale, else the same language in another region,
 * else the first non-empty entry. Returns undefined when the map is empty /
 * all values are empty strings.
 *
 * The middle step matters because the two sides name locales differently: a
 * component is translated into `en_GB`, while a shop hands us its store
 * locale — `en_US`, `en-US` or just `en`. Without it every English shop fell
 * through to the first entry, which is the German default.
 */
export function pickLocale(
    map: LocalizedString | undefined,
    preferredLocale: string | undefined,
): string | undefined {
    if (!map) return undefined;
    if (preferredLocale) {
        const direct = map[preferredLocale];
        if (typeof direct === 'string' && direct.length > 0) return direct;

        const language = preferredLocale.replace('-', '_').split('_')[0]?.toLowerCase();
        if (language) {
            for (const key of Object.keys(map)) {
                const v = map[key];
                if (key.split('_')[0]?.toLowerCase() === language && typeof v === 'string' && v.length > 0) {
                    return v;
                }
            }
        }
    }
    for (const key of Object.keys(map)) {
        const v = map[key];
        if (typeof v === 'string' && v.length > 0) return v;
    }
    return undefined;
}
